import type { MapRef } from "react-map-gl/mapbox";
import type {
	Map as MapboxMap,
	GeoJSONSourceSpecification,
	CircleLayerSpecification,
	RasterLayerSpecification,
	LineLayerSpecification,
	SymbolLayerSpecification,
} from "mapbox-gl";
import { MapBoxSource } from "@/components/Map/stores/MapBoxSource";
import { MapBoxLayer } from "@/components/Map/stores/MapBoxLayer";
import { AsyncStateStore } from "@/utils/AsyncStateStore";
import { RefObject } from "react";
import { rain, LatestImagesResponse } from "@/endpoints/rain/rain";

const emptyFC = (): GeoJSON.FeatureCollection => ({ type: "FeatureCollection", features: [] });

export const RAIN_LAYERS = {
	gaugeMarkers: "rain-gauge-markers",
	radarOverlay: "rain-radar-overlay",
	gaugeConnectionLine: "rain-gauge-connection-line",
	gaugeConnectionLabel: "rain-gauge-connection-label",
	gaugeHighlight: "rain-gauge-highlight",
} as const;

const RAIN_SOURCES = {
	gauges: "rain-gauges",
	radar: "rain-radar",
	gaugeConnection: "rain-gauge-connection",
} as const;

export class RainLayers {
	private mapRef: RefObject<MapRef | null>;
	private belowLayerId?: string;
	// @ts-expect-error tracking state, read access not yet needed
	private _attached = false;
	private styleLoadUnsub?: () => void;
	// @ts-expect-error tracking state, read access not yet needed
	private _gaugesVisible = false;
	private radarVisible = false;
	private latestImages: LatestImagesResponse | null = null;
	private latestImagesApi = new AsyncStateStore(rain.getLatestImages);

	public readonly gaugesSource = new MapBoxSource({
		id: RAIN_SOURCES.gauges,
		spec: {
			type: "geojson",
			data: emptyFC(),
		} as GeoJSONSourceSpecification,
	});

	public readonly gaugeConnectionSource = new MapBoxSource({
		id: RAIN_SOURCES.gaugeConnection,
		spec: {
			type: "geojson",
			data: emptyFC(),
		} as GeoJSONSourceSpecification,
	});

	public readonly gaugeMarkers = new MapBoxLayer({
		id: RAIN_LAYERS.gaugeMarkers,
		spec: {
			id: RAIN_LAYERS.gaugeMarkers,
			type: "circle",
			source: RAIN_SOURCES.gauges,
			paint: {
				"circle-color": "#06b6d4",
				"circle-stroke-width": 1,
				"circle-stroke-color": "#ffffff",
				"circle-radius": ["interpolate", ["linear"], ["zoom"], 6, 3, 10, 5, 14, 8, 18, 12],
			},
			layout: { visibility: "none" },
		} as CircleLayerSpecification,
	});

	public readonly gaugeConnectionLine = new MapBoxLayer({
		id: RAIN_LAYERS.gaugeConnectionLine,
		spec: {
			id: RAIN_LAYERS.gaugeConnectionLine,
			type: "line",
			source: RAIN_SOURCES.gaugeConnection,
			paint: {
				"line-color": "#06b6d4",
				"line-width": 2,
				"line-dasharray": [4, 4],
			},
			layout: { visibility: "none" },
		} as LineLayerSpecification,
	});

	public readonly gaugeConnectionLabel = new MapBoxLayer({
		id: RAIN_LAYERS.gaugeConnectionLabel,
		spec: {
			id: RAIN_LAYERS.gaugeConnectionLabel,
			type: "symbol",
			source: RAIN_SOURCES.gaugeConnection,
			filter: ["==", ["geometry-type"], "Point"],
			layout: {
				"text-field": ["get", "label"],
				"text-size": 12,
				"text-offset": [0, -1],
				visibility: "none",
			},
			paint: {
				"text-color": "#06b6d4",
				"text-halo-color": "#ffffff",
				"text-halo-width": 1.5,
			},
		} as SymbolLayerSpecification,
	});

	public readonly gaugeHighlight = new MapBoxLayer({
		id: RAIN_LAYERS.gaugeHighlight,
		spec: {
			id: RAIN_LAYERS.gaugeHighlight,
			type: "circle",
			source: RAIN_SOURCES.gaugeConnection,
			filter: ["==", ["geometry-type"], "Point"],
			paint: {
				"circle-color": "#06b6d4",
				"circle-stroke-width": 2,
				"circle-stroke-color": "#ffffff",
				"circle-radius": 8,
			},
			layout: { visibility: "none" },
		} as CircleLayerSpecification,
	});

	constructor(mapRef: RefObject<MapRef | null>, belowLayerId?: string) {
		this.mapRef = mapRef;
		this.belowLayerId = belowLayerId;
	}

	attach = async () => {
		const map = this.getMap();
		if (!map) return;

		this.gaugesSource.attach(map);
		this.gaugeConnectionSource.attach(map);
		this.gaugeMarkers.attach(map);
		this.gaugeConnectionLine.attach(map);
		this.gaugeConnectionLabel.attach(map);
		this.gaugeHighlight.attach(map);

		// Ensure rain layers are below structure markers
		this.orderLayers(map);

		this.bindStyleReload();
		this._attached = true;
	};

	detach = () => {
		this.styleLoadUnsub?.();
		this._attached = false;
	};

	setGaugeData = (geojson: GeoJSON.FeatureCollection) => {
		this.gaugesSource.setGeoJSONData(geojson);
	};

	setGaugesVisible = (visible: boolean) => {
		this._gaugesVisible = visible;
		this.gaugeMarkers.setVisibility(visible);
	};

	setRadarVisible = (visible: boolean) => {
		this.radarVisible = visible;
		const map = this.getMap();
		if (!map) return;

		if (visible) {
			this.addRadarLayer(map);
		} else {
			this.removeRadarLayer(map);
		}
	};

	loadRadarImage = async () => {
		this.latestImages = await this.latestImagesApi.fetch();
		if (this.radarVisible) {
			const map = this.getMap();
			if (map) this.addRadarLayer(map);
		}
	};

	updateRadarImage = (
		url: string,
		coordinates: [[number, number], [number, number], [number, number], [number, number]],
	) => {
		const map = this.getMap();
		if (!map) return;

		const sourceId = RAIN_SOURCES.radar;
		const source = map.getSource(sourceId);
		if (!source) return;

		// Preload image so Mapbox swaps instantly without a fade-through-blank
		const img = new Image();
		img.crossOrigin = "anonymous";
		img.onload = () => {
			// Disable fade for scrubbing, then swap
			if (map.getLayer(RAIN_LAYERS.radarOverlay)) {
				map.setPaintProperty(RAIN_LAYERS.radarOverlay, "raster-fade-duration", 0);
			}
			(source as any).updateImage({ url, coordinates });
		};
		img.src = url;
	};

	showGaugeConnection = (fromLng: number, fromLat: number, toLng: number, toLat: number, distanceKm: number) => {
		const midLng = (fromLng + toLng) / 2;
		const midLat = (fromLat + toLat) / 2;

		const fc: GeoJSON.FeatureCollection = {
			type: "FeatureCollection",
			features: [
				{
					type: "Feature",
					properties: {},
					geometry: {
						type: "LineString",
						coordinates: [
							[fromLng, fromLat],
							[toLng, toLat],
						],
					},
				},
				{
					type: "Feature",
					properties: { label: `${distanceKm.toFixed(1)} km` },
					geometry: {
						type: "Point",
						coordinates: [midLng, midLat],
					},
				},
				{
					type: "Feature",
					properties: { isGauge: true },
					geometry: {
						type: "Point",
						coordinates: [toLng, toLat],
					},
				},
			],
		};

		this.gaugeConnectionSource.setGeoJSONData(fc);
		this.gaugeConnectionLine.setVisibility(true);
		this.gaugeConnectionLabel.setVisibility(true);
		this.gaugeHighlight.setVisibility(true);
	};

	hideGaugeConnection = () => {
		this.gaugeConnectionSource.setGeoJSONData(emptyFC());
		this.gaugeConnectionLine.setVisibility(false);
		this.gaugeConnectionLabel.setVisibility(false);
		this.gaugeHighlight.setVisibility(false);
	};

	private addRadarLayer(map: MapboxMap) {
		if (!this.latestImages?.RATE) return;

		const { url, minLon, minLat, maxLon, maxLat } = this.latestImages.RATE;
		const sourceId = RAIN_SOURCES.radar;

		if (map.getSource(sourceId)) {
			// Update existing source
			(map.getSource(sourceId) as any).updateImage({ url, coordinates: this.getRadarCoordinates() });
			if (map.getLayer(RAIN_LAYERS.radarOverlay)) {
				map.setLayoutProperty(RAIN_LAYERS.radarOverlay, "visibility", "visible");
			}
			return;
		}

		map.addSource(sourceId, {
			type: "image",
			url,
			coordinates: [
				[minLon, maxLat], // top-left
				[maxLon, maxLat], // top-right
				[maxLon, minLat], // bottom-right
				[minLon, minLat], // bottom-left
			],
		});

		map.addLayer({
			id: RAIN_LAYERS.radarOverlay,
			type: "raster",
			source: sourceId,
			paint: {
				"raster-opacity": 0.5,
			},
		} as RasterLayerSpecification);

		// Place radar below the consumer's main marker layer if provided.
		if (this.belowLayerId && map.getLayer(this.belowLayerId)) {
			map.moveLayer(RAIN_LAYERS.radarOverlay, this.belowLayerId);
		}
	}

	private removeRadarLayer(map: MapboxMap) {
		if (map.getLayer(RAIN_LAYERS.radarOverlay)) {
			map.setLayoutProperty(RAIN_LAYERS.radarOverlay, "visibility", "none");
		}
	}

	private getRadarCoordinates(): [[number, number], [number, number], [number, number], [number, number]] {
		if (!this.latestImages?.RATE)
			return [
				[0, 0],
				[0, 0],
				[0, 0],
				[0, 0],
			];
		const { minLon, minLat, maxLon, maxLat } = this.latestImages.RATE;
		return [
			[minLon, maxLat],
			[maxLon, maxLat],
			[maxLon, minLat],
			[minLon, minLat],
		];
	}

	private orderLayers(map: MapboxMap) {
		// Move rain gauge markers below the consumer's main marker layer if provided.
		if (this.belowLayerId && map.getLayer(RAIN_LAYERS.gaugeMarkers) && map.getLayer(this.belowLayerId)) {
			map.moveLayer(RAIN_LAYERS.gaugeMarkers, this.belowLayerId);
		}
	}

	private getMap(): MapboxMap | null {
		const inst = this.mapRef.current;
		return inst ? (inst.getMap() as MapboxMap) : null;
	}

	private bindStyleReload() {
		const map = this.getMap();
		if (!map) return;

		const onStyleLoad = () => {
			const m = this.getMap();
			if (!m) return;

			this.gaugesSource.attach(m);
			this.gaugeConnectionSource.attach(m);
			this.gaugeMarkers.attach(m);
			this.gaugeConnectionLine.attach(m);
			this.gaugeConnectionLabel.attach(m);
			this.gaugeHighlight.attach(m);

			if (this.radarVisible && this.latestImages) {
				this.addRadarLayer(m);
			}

			this.orderLayers(m);
		};

		this.styleLoadUnsub?.();
		map.on("style.load", onStyleLoad);
		this.styleLoadUnsub = () => map.off("style.load", onStyleLoad);
	}
}
